import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SuspendedClientAlertProvider } from "@/context/SuspendedClientAlert";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import CsatSurvey from "@/pages/CsatSurvey";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Opportunita from "@/pages/Opportunita";
import OpportunitaDetail from "@/pages/OpportunitaDetail";
import Preventivi from "@/pages/Preventivi";
import PreventiviStats from "@/pages/PreventiviStats";
import Contracts from "@/pages/Contracts";
import ContractDetail from "@/pages/ContractDetail";
import Attivita from "@/pages/Attivita";
import AttivitaDetail from "@/pages/AttivitaDetail";
import Lavorazioni from "@/pages/Lavorazioni";
import LavorazioneDetail from "@/pages/LavorazioneDetail";
import Documents from "@/pages/Documents";
import Devices from "@/pages/Devices";
import Billing from "@/pages/Billing";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import TicketList from "@/pages/TicketList";
import TicketDetail from "@/pages/TicketDetail";
import Repairs from "@/pages/Repairs";
import RepairNew from "@/pages/RepairNew";
import RepairDetail from "@/pages/RepairDetail";
import Services from "@/pages/Services";
import KnowledgeBase from "@/pages/KnowledgeBase";
import KBFolder from "@/pages/KBFolder";
import KBArticle from "@/pages/KBArticle";
import Settings from "@/pages/Settings";
import Logs from "@/pages/Logs";
import Setup2FA from "@/pages/Setup2FA";
import Statistics from "@/pages/Statistics";
import Reportistica from "@/pages/Reportistica";
import AssistenzaInformaticaReport from "@/pages/AssistenzaInformaticaReport";
import ServiziReport from "@/pages/ServiziReport";
import SistemistiReport from "@/pages/SistemistiReport";
import SistemistiIndennita from "@/pages/SistemistiIndennita";
import SistemistiRiepilogoPremi from "@/pages/SistemistiRiepilogoPremi";
import Nis2 from "@/pages/Nis2";
import Iso27001 from "@/pages/Iso27001";
import Iso27001Control from "@/pages/Iso27001Control";
import ActivityCatalog from "@/pages/ActivityCatalog";
import ScadenziarioAutomezzi from "@/pages/ScadenziarioAutomezzi";
import Scadenze from "@/pages/Scadenze";
import VeicoloLogbook from "@/pages/VeicoloLogbook";
import { PortalHome, PortalContracts, PortalDevices, PortalTickets, PortalDocuments, PortalSupport, PortalPreventivi, PortalLavorazioni, PortalIso27001, PortalDispositivi, PortalRepairs } from "@/pages/Portal";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="font-heading text-lg text-muted-foreground animate-pulse">Caricamento…</div>
    </div>
  );
}

function StaffRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "client") return <Navigate to="/portale" replace />;
  return children;
}

function ClientRoute({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "client") return <Navigate to="/" replace />;
  return children;
}

function LoginGate() {
  const { user, ready } = useAuth();
  if (!ready) return <FullLoader />;
  if (user) return <Navigate to={user.role === "client" ? "/portale" : "/"} replace />;
  return <LoginPage />;
}

import { UnsavedChangesProvider } from "@/context/UnsavedChangesGuard";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SuspendedClientAlertProvider>
        <BrowserRouter>
          <ErrorBoundary>
          <UnsavedChangesProvider>
          <Routes>
            <Route path="/login" element={<LoginGate />} />
            <Route path="/csat/:token" element={<CsatSurvey />} />
            <Route path="/setup-2fa" element={<Setup2FA />} />

            <Route path="/" element={<StaffRoute><Dashboard /></StaffRoute>} />
            <Route path="/clienti" element={<StaffRoute><Clients /></StaffRoute>} />
            <Route path="/clienti/:id" element={<StaffRoute><ClientDetail /></StaffRoute>} />
            <Route path="/commerciale/opportunita" element={<StaffRoute><Opportunita /></StaffRoute>} />
            <Route path="/commerciale/opportunita/:id" element={<StaffRoute><OpportunitaDetail /></StaffRoute>} />
            <Route path="/commerciale/preventivi" element={<StaffRoute><Preventivi /></StaffRoute>} />
            <Route path="/commerciale/statistiche" element={<StaffRoute><PreventiviStats /></StaffRoute>} />
            <Route path="/contratti" element={<StaffRoute><Contracts /></StaffRoute>} />
            <Route path="/contratti/nuovo" element={<StaffRoute><ContractDetail /></StaffRoute>} />
            <Route path="/contratti/:id" element={<StaffRoute><ContractDetail /></StaffRoute>} />
            <Route path="/attivita" element={<StaffRoute><Attivita /></StaffRoute>} />
            <Route path="/attivita/:id" element={<StaffRoute><AttivitaDetail /></StaffRoute>} />
            <Route path="/interventi" element={<StaffRoute><Attivita /></StaffRoute>} />
            <Route path="/interventi/:id" element={<StaffRoute><AttivitaDetail /></StaffRoute>} />
            <Route path="/lavorazioni" element={<StaffRoute><Lavorazioni /></StaffRoute>} />
            <Route path="/lavorazioni/:id" element={<StaffRoute><LavorazioneDetail /></StaffRoute>} />
            <Route path="/documenti" element={<StaffRoute><Documents /></StaffRoute>} />
            <Route path="/dispositivi" element={<StaffRoute><Devices /></StaffRoute>} />
            <Route path="/helpdesk/ticket" element={<StaffRoute><TicketList /></StaffRoute>} />
            <Route path="/helpdesk/ticket/:id" element={<StaffRoute><TicketDetail /></StaffRoute>} />
            <Route path="/riparazioni" element={<StaffRoute><Repairs /></StaffRoute>} />
            <Route path="/riparazioni/nuova" element={<StaffRoute><RepairNew /></StaffRoute>} />
            <Route path="/riparazioni/:id" element={<StaffRoute><RepairDetail /></StaffRoute>} />
            <Route path="/servizi" element={<StaffRoute><Services /></StaffRoute>} />
            <Route path="/fatturazione" element={<StaffRoute><Billing /></StaffRoute>} />
            <Route path="/report" element={<StaffRoute><Reports /></StaffRoute>} />
            <Route path="/statistiche" element={<StaffRoute><Statistics /></StaffRoute>} />
            <Route path="/reportistica" element={<StaffRoute><Reportistica /></StaffRoute>} />
            <Route path="/reportistica/assistenza-informatica" element={<StaffRoute><AssistenzaInformaticaReport /></StaffRoute>} />
            <Route path="/reportistica/servizi" element={<StaffRoute><ServiziReport /></StaffRoute>} />
            <Route path="/reportistica/sistemisti" element={<StaffRoute><SistemistiReport /></StaffRoute>} />
            <Route path="/reportistica/sistemisti/indennita" element={<StaffRoute><SistemistiIndennita /></StaffRoute>} />
            <Route path="/reportistica/sistemisti/riepilogo-premi" element={<StaffRoute><SistemistiRiepilogoPremi /></StaffRoute>} />
            <Route path="/tools/nis2" element={<StaffRoute><Nis2 /></StaffRoute>} />
            <Route path="/tools/iso27001" element={<StaffRoute><Iso27001 /></StaffRoute>} />
            <Route path="/tools/iso27001/:cid/:code" element={<StaffRoute><Iso27001Control /></StaffRoute>} />
            <Route path="/knowledge/catalogo-attivita" element={<StaffRoute><ActivityCatalog /></StaffRoute>} />
            <Route path="/tools/catalogo-attivita" element={<Navigate to="/knowledge/catalogo-attivita" replace />} />
            <Route path="/scadenziario/automezzi" element={<StaffRoute><ScadenziarioAutomezzi /></StaffRoute>} />
            <Route path="/scadenziario/scadenze" element={<StaffRoute><Scadenze /></StaffRoute>} />
            <Route path="/scadenziario/amministrazione" element={<StaffRoute><Scadenze /></StaffRoute>} />
            <Route path="/scadenziario/clienti" element={<StaffRoute><Scadenze /></StaffRoute>} />
            <Route path="/scadenziario/automezzi/:targa/logbook" element={<StaffRoute><VeicoloLogbook /></StaffRoute>} />
            <Route path="/knowledge" element={<Navigate to="/knowledge-base" replace />} />
            <Route path="/knowledge-base" element={<StaffRoute><KnowledgeBase /></StaffRoute>} />
            <Route path="/knowledge-base/folder/:id" element={<StaffRoute><KBFolder /></StaffRoute>} />
            <Route path="/knowledge-base/article/:id" element={<StaffRoute><KBArticle /></StaffRoute>} />
            <Route path="/utenti" element={<StaffRoute><Users /></StaffRoute>} />
            <Route path="/log" element={<StaffRoute><Logs /></StaffRoute>} />
            <Route path="/impostazioni" element={<StaffRoute><Settings /></StaffRoute>} />

            <Route path="/portale" element={<ClientRoute><PortalHome /></ClientRoute>} />
            <Route path="/portale/contratti" element={<ClientRoute><PortalContracts /></ClientRoute>} />
            <Route path="/portale/preventivi" element={<ClientRoute><PortalPreventivi /></ClientRoute>} />
            <Route path="/portale/monte-ore" element={<ClientRoute><PortalSupport /></ClientRoute>} />
            <Route path="/portale/dispositivi" element={<ClientRoute><PortalDevices /></ClientRoute>} />
            <Route path="/portale/dispositivi-gestiti" element={<ClientRoute><PortalDispositivi /></ClientRoute>} />
            <Route path="/portale/documentazione" element={<ClientRoute><PortalDocuments /></ClientRoute>} />
            <Route path="/portale/ticket" element={<ClientRoute><PortalTickets /></ClientRoute>} />
            <Route path="/portale/lavorazioni" element={<ClientRoute><PortalLavorazioni /></ClientRoute>} />
            <Route path="/portale/riparazioni" element={<ClientRoute><PortalRepairs /></ClientRoute>} />
            <Route path="/portale/iso27001" element={<ClientRoute><PortalIso27001 /></ClientRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </UnsavedChangesProvider>
          </ErrorBoundary>
        </BrowserRouter>
        </SuspendedClientAlertProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
